/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TraceTrack.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions } from "../../../redux/actions";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { convertUtcToTimeZone } from "../../../utils/convertUtcToTimeZone";
import TraceTrackCards from "./TraceTrackCards";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

function TraceTrack() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profileDetails = useSelector((store) => store.profileReducer);
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  //   get intr_id from url
  const urlParams = new URLSearchParams(window.location.search);
  const intrID = urlParams.get("intr_id");
  const project_id = urlParams.get("project_id");
  const variant_id = urlParams.get("variant_id");
  const parentPage = urlParams.get("parent_page");
  const tabFromUrl = urlParams.get("tab");
  const [trackTraceDetailsList, setTrackTraceDetailsList] = useState([]);
  const [selectedTrackTraceDetails, setSelectedTrackTraceDetails] =
    useState(null);
  const [selectedTicketsTableRowData, setSelectedTicketsTableRowData] =
    useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchTickets = async (requestObj = {}) => {
    const resp = await dispatch(
      HandleApiActions({
        ...typeApprovalAPI?.get_tickets,
        params: {
          project_id: Number(project_id),
          variant_id: Number(variant_id),
          intr_id: Number(intrID),
          ...requestObj,
        },
        show_toast: false,
      }),
    );
    if (resp.error) {
      setSelectedTicketsTableRowData(null);
    } else {
      setSelectedTicketsTableRowData(resp.data[0]);
    }
  };

  useEffect(() => {
    if (intrID) fetchTickets();
  }, [intrID]);

  useEffect(() => {
    const handleGetTrackTraceDetails = async ({
      eeId,
      swmId,
      vsId,
      vcId,
      lrId,
    }) => {
      const resp = await dispatch(
        HandleApiActions({
          ...typeApprovalAPI.get_tickets_trace_track_details,
          params: {
            ...(swmId ? { swm_id: swmId } : eeId ? { ee_id: eeId } : {}),
            ...(vcId ? { vc_id: vcId } : vsId ? { vs_id: vsId } : {}),
            lr_id: lrId,
            order_column: "created_at",
            order_sort: "DESC",
            page_number: 1,
            page_size: 99,
          },
        }),
      );
      if (!resp.error) {
        const traceTraceData = resp?.data || [];
        // filter tracetrack created before and equal to current intersection
        const filteredTraceTraceData = traceTraceData?.filter((traceTrack) => {
          const createdAt = new Date(traceTrack?.created_at);
          const intersectionCreatedAt = new Date(
            selectedTicketsTableRowData?.created_at,
          );
          return createdAt <= intersectionCreatedAt;
        });
        setTrackTraceDetailsList(filteredTraceTraceData);
        setSelectedTrackTraceDetails(filteredTraceTraceData?.[0]);
      }
    };

    if (selectedTicketsTableRowData) {
      const eeId = selectedTicketsTableRowData?.ee_details?.ee_id;
      const swmId = selectedTicketsTableRowData?.swm_details?.swm_id;
      const vsId = selectedTicketsTableRowData?.vehicle_system_details?.id;
      const vcId = selectedTicketsTableRowData?.vehicle_component_details?.id;
      const lrId = selectedTicketsTableRowData?.lr_details?.lr_id;

      handleGetTrackTraceDetails({ eeId, swmId, vsId, vcId, lrId });
    }
  }, [selectedTicketsTableRowData]);

  return (
    <Box sx={{ width: "100%", p: 2 }} key={intrID}>
      <Grid container>
        <Grid item sm={12} sx={{ p: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <CommonBreadcrumbs
              items={[
                { label: "Regulatory Compliance" },
                { label: "Tickets" },
                { label: "Trace" },
              ]}
            />
            <IconButton
              onClick={() => {
                // navigate(-1);
                switch (parentPage) {
                  case "tickets":
                    navigate(
                      `/dashboard/v2/type-approval/tickets?tab=${tabFromUrl}`,
                    );
                    break;
                  case "intersections":
                    navigate(
                      `/dashboard/v2/type-approval/intersection?tab=${tabFromUrl}`,
                    );
                    break;
                  case "simulations":
                    navigate(
                      `/dashboard/doc_library/stimulation?tab=${tabFromUrl}`,
                    );
                    break;
                  default:
                    navigate(
                      `/dashboard/v2/type-approval/tickets?tab=${tabFromUrl}`,
                    );
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Grid>

        <Grid item sm={12}>
          {trackTraceDetailsList.length > 0 ? (
            trackTraceDetailsList?.map((traceTrack, index) => {
              return (
                <Accordion
                  key={index}
                  expanded={expanded === index}
                  sx={{
                    boxShadow: 3,
                    borderRadius: 0,
                    // backgroundColor: "#f9f9f9",
                    "&:before": { display: "none" },
                    p: 0,
                    "& .MuiAccordionDetails-root": {
                      p: 0,
                    },
                  }}
                  onChange={() => {
                    setSelectedTrackTraceDetails(trackTraceDetailsList[index]);
                    setExpanded(expanded === index ? false : index);
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      expanded === index ? (
                        <ExpandMoreIcon sx={{ color: "white" }} />
                      ) : (
                        <ExpandMoreIcon sx={{ color: "white" }} />
                      )
                    }
                    sx={{
                      backgroundColor: "black",
                      color: "white",
                      minHeight: "48px !important",
                      "& .MuiAccordionSummary-content": {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        p: 0,
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        // fontWeight: 600,
                        fontFamily: "Manrope, sans-serif",
                      }}
                    >
                      {`${convertUtcToTimeZone(
                        traceTrack?.created_at,
                        profileDetails?.profile?.profile?.region,
                      )?.slice(0, 5)} Intersection Id ${traceTrack?.intr_id
                        } From ${traceTrack?.swm_details?.swm_id
                          ? `Software Module - Number: ${traceTrack?.swm_details?.name}`
                          : traceTrack?.ee_details?.ee_id
                            ? `EE Component - Assembly No: ${traceTrack?.ee_details?.assembly_number}`
                            : traceTrack?.vehicle_component_details?.name
                              ? `Vehicle Component - ${traceTrack?.vehicle_component_details?.name}`
                              : traceTrack?.vehicle_system_details?.name
                                ? `Vehicle System - ${traceTrack?.vehicle_system_details?.name}`
                                : "-"
                        }`}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      backgroundColor: "white",
                      height: "430px",
                    }}
                  >
                    <TraceTrackCards
                      selectedTrackTraceDetails={selectedTrackTraceDetails}
                      projectID={project_id}
                      variantID={variant_id}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })
          ) : (
            <Typography>No Data Found</Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default TraceTrack;
